import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

const TermsConditions: React.FC = () => {
  const navigate = useNavigate();
  
  const handleBack = () => {
    // Go back if there's history, otherwise go home
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate('/');
    }
  };

  return (
    <div className="min-h-screen bg-black text-gray-100 selection:bg-cyan-500/30">
      {/* Background */}
      <div className="fixed inset-0 pointer-events-none z-0">
        <div className="absolute -top-40 -left-40 w-96 h-96 bg-purple-900/20 rounded-full blur-[100px]" />
        <div className="absolute -bottom-40 -right-40 w-96 h-96 bg-cyan-900/20 rounded-full blur-[100px]" />
      </div>

      <div className="relative z-10 max-w-4xl mx-auto px-6 py-12">
        {/* Back Button */}
        <button
          onClick={handleBack}
          className="flex items-center gap-2 text-zinc-400 hover:text-white transition-colors mb-8 group"
        >
          <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
          <span>Volver</span>
        </button>

        {/* Header */}
        <div className="mb-12">
          <h1 className="text-3xl md:text-4xl font-black text-white mb-4">
            Términos y Condiciones
          </h1>
          <p className="text-zinc-500 text-sm">
            Última actualización: {new Date().toLocaleDateString('es-AR', { year: 'numeric', month: 'long', day: 'numeric' })}
          </p>
        </div>

        {/* Content */}
        <div className="prose prose-invert prose-zinc max-w-none space-y-8">
          
          <section className="bg-zinc-900/50 border border-zinc-800 rounded-2xl p-6 md:p-8">
            <h2 className="text-xl font-bold text-white mb-4">1. Aceptación de los Términos</h2>
            <p className="text-zinc-300 leading-relaxed">
              Al acceder y utilizar <strong>LegalPromptStudio</strong> (en adelante, "la Plataforma"), 
              operada por <strong>InteligenciaLegal</strong>, aceptás estar sujeto a estos Términos y 
              Condiciones. Si no estás de acuerdo con alguna parte de estos términos, no debés utilizar 
              la Plataforma.
            </p>
            <p className="text-zinc-300 leading-relaxed mt-4">
              Estos términos se rigen exclusivamente por las leyes de la <strong>República Argentina</strong>, 
              incluyendo pero no limitándose a:
            </p>
            <ul className="list-disc list-inside text-zinc-400 mt-2 space-y-1">
              <li>Ley N° 25.326 de Protección de Datos Personales</li>
              <li>Ley N° 26.032 de Servicios de la Sociedad de la Información</li>
              <li>Código Civil y Comercial de la Nación</li>
              <li>Ley N° 24.240 de Defensa del Consumidor</li>
            </ul>
          </section>

          <section className="bg-zinc-900/50 border border-zinc-800 rounded-2xl p-6 md:p-8">
            <h2 className="text-xl font-bold text-white mb-4">2. Descripción del Servicio</h2>
            <p className="text-zinc-300 leading-relaxed">
              LegalPromptStudio es una herramienta de asistencia basada en inteligencia artificial 
              diseñada para ayudarte a optimizar y mejorar prompts relacionados con el ámbito jurídico. 
              El servicio incluye:
            </p>
            <ul className="list-disc list-inside text-zinc-400 mt-4 space-y-2">
              <li>Análisis de prompts legales</li>
              <li>Sugerencias de mejora basadas en mejores prácticas</li>
              <li>Generación de prompts optimizados</li>
              <li>Historial de análisis previos</li>
            </ul>
          </section>

          <section className="bg-zinc-900/50 border border-zinc-800 rounded-2xl p-6 md:p-8">
            <h2 className="text-xl font-bold text-white mb-4">3. Naturaleza del Servicio - Descargo de Responsabilidad</h2>
            <div className="bg-amber-950/30 border border-amber-900/50 rounded-xl p-4 mb-4">
              <p className="text-amber-200 font-medium">
                ⚠️ IMPORTANTE: Esta herramienta NO constituye asesoramiento legal profesional.
              </p>
            </div>
            <p className="text-zinc-300 leading-relaxed">
              Tenés que entender y aceptar que:
            </p>
            <ul className="list-disc list-inside text-zinc-400 mt-4 space-y-2">
              <li>
                Los resultados generados por la IA son <strong>orientativos</strong> y no reemplazan 
                el consejo de un profesional del derecho matriculado.
              </li>
              <li>
                InteligenciaLegal no se responsabiliza por decisiones tomadas en base a los 
                resultados proporcionados por la Plataforma.
              </li>
              <li>
                Siempre debés consultar con un abogado o profesional legal calificado antes 
                de tomar decisiones jurídicas importantes.
              </li>
              <li>
                La IA puede generar información inexacta o desactualizada. Es tu responsabilidad 
                verificar toda la información.
              </li>
            </ul>
          </section>

          <section className="bg-zinc-900/50 border border-zinc-800 rounded-2xl p-6 md:p-8">
            <h2 className="text-xl font-bold text-white mb-4">4. Uso Aceptable</h2>
            <p className="text-zinc-300 leading-relaxed">
              Al utilizar la Plataforma, te comprometés a:
            </p>
            <ul className="list-disc list-inside text-zinc-400 mt-4 space-y-2">
              <li>No utilizar el servicio para actividades ilegales o fraudulentas.</li>
              <li>No intentar vulnerar la seguridad de la Plataforma.</li>
              <li>No compartir tu cuenta con terceros.</li>
              <li>No sobrecargar el sistema con solicitudes automatizadas (bots, scripts, etc.).</li>
              <li>No utilizar el servicio para generar contenido que viole derechos de terceros.</li>
              <li>Respetar los límites de uso establecidos.</li>
            </ul>
          </section>

          <section className="bg-zinc-900/50 border border-zinc-800 rounded-2xl p-6 md:p-8">
            <h2 className="text-xl font-bold text-white mb-4">5. Registro y Cuenta</h2>
            <p className="text-zinc-300 leading-relaxed">
              Para acceder a la Plataforma, debés autenticarte mediante Google. Al hacerlo:
            </p>
            <ul className="list-disc list-inside text-zinc-400 mt-4 space-y-2">
              <li>Autorizás a InteligenciaLegal a acceder a tu información básica de perfil (nombre, email, foto).</li>
              <li>Sos responsable de mantener la seguridad de tu cuenta de Google.</li>
              <li>Debés notificarnos inmediatamente si sospechás de un acceso no autorizado.</li>
            </ul>
          </section>

          <section className="bg-zinc-900/50 border border-zinc-800 rounded-2xl p-6 md:p-8">
            <h2 className="text-xl font-bold text-white mb-4">6. Propiedad Intelectual</h2>
            <p className="text-zinc-300 leading-relaxed">
              Todos los derechos de propiedad intelectual sobre la Plataforma, incluyendo su diseño, 
              código, logos, y contenido original, pertenecen a InteligenciaLegal.
            </p>
            <p className="text-zinc-300 leading-relaxed mt-4">
              Respecto a tu contenido:
            </p>
            <ul className="list-disc list-inside text-zinc-400 mt-2 space-y-2">
              <li>Conservás todos los derechos sobre los prompts que ingresás.</li>
              <li>Otorgás a InteligenciaLegal una licencia limitada para procesar tu contenido 
                  únicamente con el fin de brindarte el servicio.</li>
              <li>Los resultados generados pueden ser utilizados libremente por vos.</li>
            </ul>
          </section>

          <section className="bg-zinc-900/50 border border-zinc-800 rounded-2xl p-6 md:p-8">
            <h2 className="text-xl font-bold text-white mb-4">7. Gratuidad y Modificaciones</h2>
            <p className="text-zinc-300 leading-relaxed">
              La Plataforma actualmente se ofrece de forma <strong>gratuita</strong>. InteligenciaLegal 
              se reserva el derecho de:
            </p>
            <ul className="list-disc list-inside text-zinc-400 mt-4 space-y-2">
              <li>Modificar, suspender o discontinuar el servicio en cualquier momento.</li>
              <li>Implementar planes de pago en el futuro, notificando a los usuarios con anticipación.</li>
              <li>Establecer límites de uso para garantizar la calidad del servicio.</li>
              <li>Actualizar estos términos, siendo efectivos desde su publicación.</li>
            </ul>
          </section>

          <section className="bg-zinc-900/50 border border-zinc-800 rounded-2xl p-6 md:p-8">
            <h2 className="text-xl font-bold text-white mb-4">8. Limitación de Responsabilidad</h2>
            <p className="text-zinc-300 leading-relaxed">
              En la máxima medida permitida por la ley argentina, InteligenciaLegal no será 
              responsable por:
            </p>
            <ul className="list-disc list-inside text-zinc-400 mt-4 space-y-2">
              <li>Daños directos, indirectos, incidentales o consecuentes derivados del uso de la Plataforma.</li>
              <li>Pérdida de datos o interrupción del servicio.</li>
              <li>Errores, inexactitudes u omisiones en los resultados generados.</li>
              <li>Decisiones legales o comerciales tomadas en base a la información proporcionada.</li>
            </ul>
          </section>

          <section className="bg-zinc-900/50 border border-zinc-800 rounded-2xl p-6 md:p-8">
            <h2 className="text-xl font-bold text-white mb-4">9. Ley Aplicable y Jurisdicción</h2>
            <p className="text-zinc-300 leading-relaxed">
              Estos términos se rigen por las leyes de la <strong>República Argentina</strong>. 
              Para cualquier controversia que surja de la interpretación o cumplimiento de estos 
              términos, las partes se someten a la jurisdicción de los <strong>Tribunales Ordinarios 
              de la Ciudad Autónoma de Buenos Aires</strong>, renunciando a cualquier otro fuero 
              que pudiera corresponderles.
            </p>
          </section>

          <section className="bg-zinc-900/50 border border-zinc-800 rounded-2xl p-6 md:p-8">
            <h2 className="text-xl font-bold text-white mb-4">10. Contacto</h2>
            <p className="text-zinc-300 leading-relaxed">
              Si tenés preguntas sobre estos Términos y Condiciones, podés contactarnos a través de:
            </p>
            <ul className="list-none text-zinc-400 mt-4 space-y-2">
              <li>🌐 Web: <a href="https://inteligencialegal.com.ar" target="_blank" rel="noopener noreferrer" className="text-cyan-400 hover:text-cyan-300 transition-colors">inteligencialegal.com.ar</a></li>
              <li>📸 Instagram: <a href="https://instagram.com/inteligencialegal.ok" target="_blank" rel="noopener noreferrer" className="text-cyan-400 hover:text-cyan-300 transition-colors">@inteligencialegal.ok</a></li>
            </ul>
          </section>

        </div>

        {/* Footer */}
        <div className="mt-12 pt-8 border-t border-zinc-800 text-center">
          <p className="text-zinc-600 text-sm">
            © {new Date().getFullYear()} InteligenciaLegal. Todos los derechos reservados.
          </p>
        </div>
      </div>
    </div>
  );
};

export default TermsConditions;
